import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CardBox } from '@/components/ui/CardBox';
import { Icon } from '@/components/ui/Icon';
import { mdiAlertCircle, mdiImport } from '@mdi/js';
import * as React from 'react';
import { JobHistory } from './components/JobHistory';
import { JobProgress } from './components/JobProgress';
import { UrlInput } from './components/UrlInput';
import {
  useImportJobMutations,
  useImportJobProgress,
  useImportJobs,
  useProviders,
} from './hooks/useImportJobs';

type ImportStep = 'input' | 'progress';

export const ImportPage: React.FC = () => {
  const [currentStep, setCurrentStep] = React.useState<ImportStep>('input');
  const [selectedProvider, setSelectedProvider] =
    React.useState<string>('google_maps');
  const [urls, setUrls] = React.useState<string>('');
  const [selectedStatus, setSelectedStatus] = React.useState<string>('Active');
  const [activeJobId, setActiveJobId] = React.useState<number | null>(null);

  const { data: providers = [], isLoading: isLoadingProviders } =
    useProviders();
  const { data: jobs = [] } = useImportJobs();
  const { data: activeJob } = useImportJobProgress(
    activeJobId,
    currentStep === 'progress',
  );

  const { createJob, pauseJob, resumeJob, cancelJob, retryFailed } =
    useImportJobMutations();

  const parsedUrls = React.useMemo(() => {
    if (!urls.trim()) return [];
    return urls
      .split(/[\n]+/)
      .map(url => url.trim())
      .filter(url => url.length > 0 && url.startsWith('http'));
  }, [urls]);

  const handleStartImport = async () => {
    if (parsedUrls.length === 0) return;

    const result = await createJob.mutateAsync({
      provider: selectedProvider,
      urls: parsedUrls,
      businessStatus: selectedStatus,
    });

    setActiveJobId(result.id);
    setCurrentStep('progress');
  };

  const handleSelectJob = (jobId: number) => {
    setActiveJobId(jobId);
    setCurrentStep('progress');
  };

  const handleNewImport = () => {
    setUrls('');
    setActiveJobId(null);
    setSelectedStatus('Active');
    setCurrentStep('input');
  };

  const selectedProviderData = providers.find(
    p => p.identifier === selectedProvider,
  );
  const isConfigured = selectedProviderData?.configured ?? false;

  if (isLoadingProviders) {
    return (
      <DashboardLayout>
        <div className='flex items-center justify-center h-64'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600' />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <ProtectedRoute action='create' subject='Business'>
        <div className='max-w-4xl mx-auto'>
          <div className='mb-6'>
            <h1 className='text-2xl font-bold text-grey-900 flex items-center'>
              <Icon path={mdiImport} size={28} className='mr-2' />
              Import Businesses
            </h1>
            <p className='text-grey-500 mt-1'>
              Bulk import businesses from map providers
            </p>
          </div>

          {providers.length === 0 || !isConfigured ? (
            <CardBox>
              <div className='text-center py-8'>
                <Icon
                  path={mdiAlertCircle}
                  size={48}
                  className='text-yellow-500 mx-auto mb-4'
                />
                <h2 className='text-lg font-medium text-grey-900 mb-2'>
                  No Map Provider Configured
                </h2>
                <p className='text-grey-500 max-w-md mx-auto'>
                  Please configure a map provider API key (e.g.,
                  GOOGLE_MAPS_API_KEY) to enable business imports.
                </p>
              </div>
            </CardBox>
          ) : (
            <>
              <CardBox>
                {currentStep === 'input' && (
                  <UrlInput
                    providers={providers}
                    selectedProvider={selectedProvider}
                    onProviderChange={setSelectedProvider}
                    urls={urls}
                    onUrlsChange={setUrls}
                    selectedStatus={selectedStatus}
                    onStatusChange={setSelectedStatus}
                    onSubmit={handleStartImport}
                    isLoading={createJob.isPending}
                  />
                )}

                {currentStep === 'progress' && activeJob && (
                  <JobProgress
                    job={activeJob}
                    onPause={() => pauseJob.mutate(activeJob.id)}
                    onResume={() => resumeJob.mutate(activeJob.id)}
                    onCancel={() => cancelJob.mutate(activeJob.id)}
                    onRetry={() => retryFailed.mutate(activeJob.id)}
                    onNewImport={handleNewImport}
                    isPausing={pauseJob.isPending}
                    isResuming={resumeJob.isPending}
                    isCancelling={cancelJob.isPending}
                    isRetrying={retryFailed.isPending}
                  />
                )}
              </CardBox>

              {currentStep === 'input' && jobs.length > 0 && (
                <JobHistory
                  jobs={jobs}
                  onSelectJob={handleSelectJob}
                  selectedJobId={activeJobId}
                />
              )}
            </>
          )}
        </div>
      </ProtectedRoute>
    </DashboardLayout>
  );
};
